import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  Address,
  CheckoutPreview,
  MyIdentities,
  ProductCard,
  ProductDetail,
  SessionUser,
} from "@zx/contracts";

import { api, qs, resetAuthRefresh } from "./client.ts";
import { noteLoggedIn, resetSessionState } from "../lib/session.ts";
import { usePriceView } from "../lib/identity.ts";

/**
 * 取数入口。服务端数据一律走这里，不在 zustand 里镜像一份。
 *
 * 🔴 **前端没有"商品价格"这种静态数据。** 同一个商品，挂在不同分销商
 *    名下的用户看到的是不同的价 —— 所以任何缓存都必须按当前会话隔离，
 *    切换账号时要整体失效（见 `useLogout`）。
 */

interface Page<T> {
  items: T[];
  nextCursor: string | null;
}

/**
 * 🔴 **带价格的查询，key 里必须带上当前的价格视角。**
 *    同一个商品在"我进货""客户看到""陌生人看到"三种视角下是三个不同的数 ——
 *    共用一个缓存条目的话，切视角只会看到上一次的价，
 *    而且刷新时机稍有错位就会**长期停在错的价上**。
 */
export const keys = {
  me: ["me"] as const,
  identities: ["me", "identities"] as const,
  addresses: ["me", "addresses"] as const,
  categories: ["categories"] as const,
  products: (q: Record<string, unknown>, view: string) => ["products", view, q] as const,
  product: (id: number | string, view: string) => ["product", view, id] as const,
  cart: (view: string) => ["cart", view] as const,
  orders: (q: Record<string, unknown>) => ["orders", q] as const,
  order: (id: number) => ["order", id] as const,
  cases: (q: Record<string, unknown>, view: string) => ["cases", view, q] as const,
  caseDetail: (slug: string, view: string) => ["case", view, slug] as const,
};

// ─────────────────────────── 会话 ───────────────────────────

export function useMe() {
  return useQuery({
    queryKey: keys.me,
    queryFn: () =>
      api.get<{ user: SessionUser }>("/auth/me").then((r) => {
        /** 记一笔"这个会话登录过" —— 顶部的过期提示只对登录过的人弹。 */
        noteLoggedIn();
        return r.user;
      }),
    // 401 是"没登录"，不是错误，不要重试
    retry: false,
    staleTime: 60_000,
  });
}

export function useIdentities(enabled = true) {
  return useQuery({
    queryKey: keys.identities,
    queryFn: () => api.get<MyIdentities>("/auth/me/identities"),
    enabled,
    retry: false,
  });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { identifier: string; password: string }) =>
      api.post<{ user: SessionUser }>("/auth/login", input),
    onSuccess: () => {
      /**
       * ⚠️ 新会话要允许重新续期 —— 上一次续期失败时会立一个"这浏览器没登录"
       *    的标记，不清掉的话新登录之后 access token 过期就再也续不上了。
       */
      resetAuthRefresh();
      /** 🔴 登录后整体失效：价格随身份变，旧缓存全是别人的价。 */
      qc.invalidateQueries();
    },
  });
}

export function useRegister() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      phone: string;
      password: string;
      nickname?: string;
      inviteCode?: string;
      visitId?: number;
    }) => api.post<{ user: SessionUser; binding: { orgId: number | null } }>("/auth/register", input),
    onSuccess: () => {
      resetAuthRefresh();
      qc.invalidateQueries();
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post("/auth/logout"),
    onSuccess: () => {
      /** 主动退出不是过期，下次进来当新会话 —— 否则会弹一条莫名其妙的提示。 */
      resetSessionState();
      qc.clear();
    },
  });
}

// ─────────────────────────── 商品 ───────────────────────────

export function useCategories() {
  return useQuery({
    queryKey: keys.categories,
    queryFn: () => api.get<{ id: number; name: string; slug: string; parentId: number | null }[]>("/categories"),
    staleTime: 5 * 60_000,
  });
}

export function useProducts(
  q: {
    categoryId?: number;
    keyword?: string;
    space?: string;
    sort?: string;
    limit?: number;
    /**
     * 在哪家店里看。
     * ⚠️ 只对**没有归属**的访问者改变价格 —— 已有归属的人永远按自己的链算，
     *    否则展示的价和下单扣的钱会对不上。
     */
    shopId?: number;
    /**
     * 按**这家店自己的分类**筛（「主推」「工装常用」）。
     * ⚠️ 跟 `categoryId`（全站品类：瓷砖/地板/灯具）是两回事，别混。
     */
    shopCategoryId?: number;
  },
  enabled = true,
) {
  const view = usePriceView();
  return useQuery({
    queryKey: keys.products(q, view),
    queryFn: () =>
      api.get<Page<ProductCard>>(
        `/products${qs(q as Record<string, string | number | undefined>)}`,
      ),
    enabled,
  });
}

export function useProduct(id: number | string, shopId?: number) {
  const view = usePriceView();
  return useQuery({
    queryKey: [...keys.product(id, view), shopId ?? null],
    queryFn: () =>
      api.get<ProductDetail>(`/products/${id}${shopId ? `?shopId=${shopId}` : ""}`),
    enabled: Boolean(id),
  });
}

// ─────────────────────────── 购物车 ───────────────────────────

export function useCart(enabled = true) {
  const view = usePriceView();
  return useQuery({
    queryKey: keys.cart(view),
    queryFn: () => api.get<CheckoutPreview>("/cart"),
    enabled,
    retry: false,
  });
}

export function useAddToCart() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { skuId: number; quantity: number }) => api.post("/cart", input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["cart"] }),
  });
}

export function useSetCartQuantity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ skuId, quantity }: { skuId: number; quantity: number }) =>
      api.patch(`/cart/${skuId}`, { quantity }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["cart"] }),
  });
}

export function useRemoveFromCart() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (skuId: number) => api.del(`/cart/${skuId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["cart"] }),
  });
}

// ─────────────────────────── 地址与下单 ───────────────────────────

export type { Address };

export function useAddresses(enabled = true) {
  return useQuery({
    queryKey: keys.addresses,
    queryFn: () => api.get<Address[]>("/auth/me/addresses"),
    enabled,
    retry: false,
  });
}

export function useSaveAddress() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Omit<Address, "id"> & { id?: number }) =>
      api.post<Address>("/auth/me/addresses", input),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.addresses }),
  });
}

export function useCheckoutPreview() {
  return useMutation({
    mutationFn: (input: {
      items: { skuId: number; quantity: number }[];
      couponCode?: string;
    }) => api.post<CheckoutPreview>("/checkout/preview", input),
  });
}

export function useCreateOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      items: { skuId: number; quantity: number }[];
      addressId: number;
      buyerNote?: string;
      idempotencyKey: string;
      couponCode?: string;
      /** 🔴 结算页显示的应付合计，服务端要比对。 */
      expectedTotal?: number;
    }) =>
      api.post<{
        reused: boolean;
        orders: { id: number; orderNo: string; payableAmount: number }[];
      }>("/orders", input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cart"] });
      qc.invalidateQueries({ queryKey: ["orders"] });
    },
  });
}

// ─────────────────────────── 订单 ───────────────────────────

export interface OrderRow {
  id: number;
  orderNo: string;
  payment: string;
  fulfillment: string;
  goodsAmount: number;
  freightAmount: number;
  payableAmount: number;
  createdAt: string;
  items: {
    id: number;
    titleSnapshot: string;
    unitPrice: number;
    quantity: number;
    subtotal: number;
    imageKeySnapshot: string | null;
    /** ⚠️ 服务端已把对象存储 key 转成地址，前端别自己拼。 */
    imageUrl: string | null;
    specsSnapshot: Record<string, string>;
  }[];
}

/**
 * 订单页签 → 查询条件。
 *
 * 🔴 **这一份映射两套版面共用。**
 *    「我的」页上的四个入口传的是 `status=pending|paid|shipped|done`，
 *    而接口收的是 `payment` / `fulfillment` 两个正交的状态 ——
 *    翻译写两份的话迟早走岔（点「待发货」出来一堆待付款的单）。
 *
 * ⚠️ 「已完成」覆盖 `delivered` 和 `completed` 两种：对买家来说
 *    "货收到了"和"这单结清了"都是结束了，分开列没有意义。
 */
export function ordersQueryFor(status?: string): { payment?: string; fulfillment?: string } {
  switch (status) {
    case "pending":
      return { payment: "pending" };
    case "paid":
      return { payment: "paid", fulfillment: "pending" };
    case "shipped":
      return { fulfillment: "shipped" };
    case "done":
      return { fulfillment: "delivered,completed" };
    default:
      return {};
  }
}

export function useOrders(
  q: { payment?: string; fulfillment?: string; limit?: number } = {},
  enabled = true,
) {
  return useQuery({
    queryKey: keys.orders(q),
    queryFn: () => api.get<Page<OrderRow>>(`/orders${qs(q as Record<string, string | number | undefined>)}`),
    enabled,
    retry: false,
  });
}

export function useOrder(id: number) {
  return useQuery({
    queryKey: keys.order(id),
    queryFn: () => api.get<OrderRow>(`/orders/${id}`),
    enabled: Boolean(id),
  });
}

export function useConfirmReceipt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (orderId: number) => api.post(`/orders/${orderId}/confirm`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["orders"] }),
  });
}

export function useCancelOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (orderId: number) => api.post(`/orders/${orderId}/cancel`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["orders"] }),
  });
}

// ─────────────────────────── 装修案例 ───────────────────────────

export interface CaseCard {
  id: number;
  slug: string;
  title: string;
  coverUrl: string;
  spaces: string[];
  style: string;
  areaSqm: number | null;
  budget: number | null;
  viewCount: number;
}

export interface CaseDetail extends CaseCard {
  contentMd: string;
  imageUrls: string[];
  /** 🔴 价格是服务端按我的归属链现算的，不是案例里存的静态值。 */
  products: { id: number; title: string; imageUrl: string | null; fromPrice: number }[];
}

export function useCases(q: { space?: string; style?: string } = {}) {
  const view = usePriceView();
  const qs = new URLSearchParams();
  if (q.space) qs.set("space", q.space);
  if (q.style) qs.set("style", q.style);
  return useQuery({
    queryKey: keys.cases(q, view),
    queryFn: () =>
      api.get<{ items: CaseCard[]; nextCursor: string | null }>(
        `/cases${qs.toString() ? `?${qs}` : ""}`,
      ),
  });
}

export function useCaseStyles() {
  return useQuery({
    queryKey: ["case-styles"],
    queryFn: () => api.get<string[]>("/cases/styles"),
    staleTime: 5 * 60_000,
  });
}

export function useCaseDetail(slug: string) {
  const view = usePriceView();
  return useQuery({
    queryKey: keys.caseDetail(slug, view),
    queryFn: () => api.get<CaseDetail>(`/cases/${slug}`),
    enabled: Boolean(slug),
  });
}

// ─────────────────────────── 退款 ───────────────────────────

export interface RefundRow {
  id: number;
  refundNo: string;
  amount: number;
  status: "pending" | "done" | "rejected";
  reason: string;
  createdAt: string;
}

export function useOrderRefunds(orderId: number, enabled = true) {
  return useQuery({
    queryKey: ["refunds", orderId],
    queryFn: () => api.get<RefundRow[]>(`/orders/${orderId}/refunds`),
    enabled,
  });
}

export function useRequestRefund(orderId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { reason: string; amount?: number }) =>
      api.post<RefundRow>(`/orders/${orderId}/refunds`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["refunds", orderId] });
      void qc.invalidateQueries({ queryKey: ["orders"] });
    },
  });
}
